import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { useQuery } from '@tanstack/react-query';
import { initSupabase } from '@/api/adapters/SupabaseAdapter';
import { Building2, Save, X, Plus, Users, Trash2, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import StarRating from './StarRating';
import { getKundentypOptions, addCustomKundentyp } from '@/lib/kundentyp';
import { base44 } from '@/api/base44Client';
import { toast } from 'sonner';
import { findDuplicateCustomer, isCustomerStatus, isLostStatus, LEAD_STATUSES, normalizeLeadStatus } from '@/lib/leadStatus';
import { useAuth } from '@/api/AuthContext';

const BRANCHEN = [
  'Asiatische Küche',
  'Bowl/Smoothies',
  'Burger',
  'Döner',
  'Indische Küche',
  'Italienische Küche',
  'Hotel',
  'Industrie'
];

export default function LeadForm({ lead, onSubmit, onCancel, defaultKundentyp = 'B2B' }) {
  const { isAdmin } = useAuth();
  // Beim Umschalten auf eigene Zustellung: offene Bestellungen, die schon in
  // Shopify liegen, bekommen dort evtl. trotzdem ein DPD-Etikett → Hinweis.
  const wechseltAufEigen = !!lead?.id && lead?.shopify_versand !== false;
  const [formData, setFormData] = useState({
    name: lead?.name || '',
    zusatz: lead?.zusatz || '',
    branche: lead?.branche || '',
    strasse: lead?.strasse || '',
    plz: lead?.plz || '',
    ort: lead?.ort || '',
    land: lead?.land || 'Deutschland',
    telefon: lead?.telefon || '',
    email: lead?.email || '',
    notes: lead?.notes || '',
    status: lead?.status ? normalizeLeadStatus(lead.status) : 'Potenzieller Interessent',
    rating: lead?.rating || 0,
    // Versand über Shopify (DPD-Etikett); false = eigene Zustellung — nur Admin
    shopify_versand: lead?.shopify_versand !== false,
    // Kundentyp: agent kaydında otomatik B2B (defaultKundentyp)
    kundentyp: lead?.kundentyp || defaultKundentyp,
    // Lieferanschrift (Rechnungsanschrift'ten farklıysa)
    liefer_abweichend: lead?.liefer_abweichend || false,
    liefer_strasse: lead?.liefer_strasse || '',
    liefer_plz: lead?.liefer_plz || '',
    liefer_ort: lead?.liefer_ort || '',
    liefer_land: lead?.liefer_land || 'Deutschland',
    // Zahlungsbedingungen (Standard: Skonto 0 %, 0 Tage · Rabatt 0 % · Ziel 5 Tage)
    skonto_prozent: lead?.skonto_prozent ?? 0,
    skonto_tage: lead?.skonto_tage ?? 0,
    kundenrabatt_prozent: lead?.kundenrabatt_prozent ?? 0,
    zahlungsziel_tage: lead?.zahlungsziel_tage ?? 5,
  });
  // Ansprechpartner (opsiyonel) — einklappbar, "+" ile açılır
  const [ansprechpartner, setAnsprechpartner] = useState(() => {
    let ap = lead?.ansprechpartner || [];
    if (typeof ap === 'string') { try { ap = JSON.parse(ap); } catch { ap = []; } }
    return Array.isArray(ap) ? ap : [];
  });
  const [apOpen, setApOpen] = useState(
    () => Array.isArray(lead?.ansprechpartner) && lead.ansprechpartner.length > 0
  );

  const addAnsprechpartner = () => {
    setApOpen(true);
    setAnsprechpartner((prev) => [...prev, { name: '', position: '', telefon: '', email: '' }]);
  };
  const updateAnsprechpartner = (idx, field, value) => {
    setAnsprechpartner((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  };
  const removeAnsprechpartner = (idx) => {
    setAnsprechpartner((prev) => prev.filter((_, i) => i !== idx));
  };
  const [kundentypOptions, setKundentypOptions] = useState(getKundentypOptions());
  const [newTypeMode, setNewTypeMode] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [isChecking, setIsChecking] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddCustomType = () => {
    const name = newTypeName.trim();
    if (!name) return;
    addCustomKundentyp(name);
    setKundentypOptions(getKundentypOptions());
    handleChange('kundentyp', name);
    setNewTypeMode(false);
    setNewTypeName('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isChecking) return;
    setIsChecking(true);
    try {
      // Duplikat-Regel: gleicher Name + gleiche Adresse → blockieren.
      // Gleicher Name mit ABWEICHENDER Adresse ist erlaubt.
      const allLeads = await base44.entities.Lead.list();
      const duplicate = findDuplicateCustomer(allLeads || [], {
        name: formData.name,
        strasse: formData.strasse,
        plz: formData.plz,
        ort: formData.ort,
        excludeId: lead?.id || null,
      });
      if (duplicate) {
        toast.error('Kunde existiert bereits mit gleicher Adresse — Doppelanlage nicht erlaubt.');
        return;
      }
    } catch (err) {
      console.error('Duplikatprüfung fehlgeschlagen:', err);
      // Bei Prüf-Fehler nicht blockieren
    } finally {
      setIsChecking(false);
    }
    // Boş Ansprechpartner satırları filtrelenir — alan tamamen opsiyonel
    const cleanAp = ansprechpartner.filter(
      (p) => (p.name || '').trim() || (p.telefon || '').trim() || (p.email || '').trim()
    );
    // shopify_versand nur senden, wenn ein Admin es wirklich geändert hat —
    // so speichern Agenten unverändert (auch falls die Spalte noch fehlt).
    const { shopify_versand: versand, ...rest } = formData;
    const versandGeaendert = isAdmin && versand !== (lead?.shopify_versand !== false);
    onSubmit({ ...rest, ...(versandGeaendert ? { shopify_versand: versand } : {}), ansprechpartner: cleanAp });
  };

  const isEditing = !!lead?.id;

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-slate-600" />
          {isEditing ? 'Kontakt bearbeiten' : 'Neuer Kontakt'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Firmenname"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="branche">Branche *</Label>
              <Select value={formData.branche} onValueChange={(v) => handleChange('branche', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Branche wählen" />
                </SelectTrigger>
                <SelectContent>
                  {BRANCHEN.map(b => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="zusatz">Zusatz</Label>
              <Input
                id="zusatz"
                value={formData.zusatz}
                onChange={(e) => handleChange('zusatz', e.target.value)}
                placeholder="Zusätzliche Info"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="strasse">Straße / Nr.</Label>
              <Input
                id="strasse"
                value={formData.strasse}
                onChange={(e) => handleChange('strasse', e.target.value)}
                placeholder="Musterstraße 123"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="plz">PLZ</Label>
              <Input
                id="plz"
                value={formData.plz}
                onChange={(e) => handleChange('plz', e.target.value)}
                placeholder="12345"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ort">Ort</Label>
              <Input
                id="ort"
                value={formData.ort}
                onChange={(e) => handleChange('ort', e.target.value)}
                placeholder="München"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="land">Land</Label>
              <Input
                id="land"
                value={formData.land}
                onChange={(e) => handleChange('land', e.target.value)}
                placeholder="Deutschland"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="telefon">Telefon</Label>
              <Input
                id="telefon"
                type="tel"
                value={formData.telefon}
                onChange={(e) => handleChange('telefon', e.target.value)}
                placeholder="+49 123 456789"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="email">E-Mail *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="kontakt@firma.de"
                required
              />
              {!formData.email && (
                <p className="text-xs text-red-600 flex items-center gap-1">
                  ⚠️ E-Mail-Adresse ist ein Pflichtfeld
                </p>
              )}
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="kundentyp">Kundentyp *</Label>
              {newTypeMode ? (
                <div className="flex gap-2">
                  <Input
                    value={newTypeName}
                    onChange={(e) => setNewTypeName(e.target.value)}
                    placeholder="Neuer Marktplatz (z. B. Amazon)"
                    autoFocus
                  />
                  <Button type="button" size="sm" onClick={handleAddCustomType}>
                    <Plus className="w-4 h-4" />
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => setNewTypeMode(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Select
                  value={formData.kundentyp}
                  onValueChange={(v) => (v === '__new__' ? setNewTypeMode(true) : handleChange('kundentyp', v))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Kundentyp wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {kundentypOptions.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                    <SelectItem value="__new__">＋ Weiteren Marktplatz hinzufügen…</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            {isAdmin && (
              <VersandSchalter
                checked={formData.shopify_versand}
                onChange={(v) => handleChange('shopify_versand', v)}
                leadId={wechseltAufEigen && !formData.shopify_versand ? lead.id : null}
              />
            )}

            {/* Status: normalerweise automatisch (erste Bestellung → Kunde);
                Admins können ihn direkt setzen, z. B. für den Kundenportal-Zugang. */}
            {isAdmin && (
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                  <SelectTrigger id="status"><SelectValue placeholder="Status wählen" /></SelectTrigger>
                  <SelectContent>
                    {LEAD_STATUSES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-400">Wird bei der ersten Bestellung automatisch „Kunde“. Kundenportal-Zugang nur ab „Kunde“.</p>
              </div>
            )}

            <div className="md:col-span-2 flex items-center gap-2 pt-1">
              <Checkbox
                id="liefer_abweichend"
                checked={formData.liefer_abweichend}
                onCheckedChange={(v) => handleChange('liefer_abweichend', !!v)}
              />
              <Label htmlFor="liefer_abweichend" className="cursor-pointer">
                Lieferanschrift weicht von Rechnungsanschrift ab
              </Label>
            </div>

            {formData.liefer_abweichend && (
              <>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="liefer_strasse">Lieferanschrift — Straße / Nr.</Label>
                  <Input
                    id="liefer_strasse"
                    value={formData.liefer_strasse}
                    onChange={(e) => handleChange('liefer_strasse', e.target.value)}
                    placeholder="Lieferstraße 1"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="liefer_plz">PLZ</Label>
                  <Input
                    id="liefer_plz"
                    value={formData.liefer_plz}
                    onChange={(e) => handleChange('liefer_plz', e.target.value)}
                    placeholder="12345"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="liefer_ort">Ort</Label>
                  <Input
                    id="liefer_ort"
                    value={formData.liefer_ort}
                    onChange={(e) => handleChange('liefer_ort', e.target.value)}
                    placeholder="Berlin"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="liefer_land">Land</Label>
                  <Input
                    id="liefer_land"
                    value={formData.liefer_land}
                    onChange={(e) => handleChange('liefer_land', e.target.value)}
                    placeholder="Deutschland"
                  />
                </div>
              </>
            )}

            {/* Zahlungsbedingungen */}
            <div className="md:col-span-2 pt-2 border-t">
              <p className="text-sm font-semibold text-slate-700 mb-3">Zahlungsbedingungen</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="skonto_prozent" className="text-xs">Skonto (%)</Label>
                  <Input
                    id="skonto_prozent"
                    type="number" step="0.1" min="0" max="100"
                    value={formData.skonto_prozent}
                    onChange={(e) => handleChange('skonto_prozent', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="skonto_tage" className="text-xs">Skonto Tage</Label>
                  <Input
                    id="skonto_tage"
                    type="number" step="1" min="0"
                    value={formData.skonto_tage}
                    onChange={(e) => handleChange('skonto_tage', e.target.value === '' ? 0 : parseInt(e.target.value, 10))}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="kundenrabatt_prozent" className="text-xs">Kundenrabatt (%)</Label>
                  <Input
                    id="kundenrabatt_prozent"
                    type="number" step="0.1" min="0" max="100"
                    value={formData.kundenrabatt_prozent}
                    onChange={(e) => handleChange('kundenrabatt_prozent', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="zahlungsziel_tage" className="text-xs">Zahlungsziel (Tage)</Label>
                  <Input
                    id="zahlungsziel_tage"
                    type="number" step="1" min="0"
                    value={formData.zahlungsziel_tage}
                    onChange={(e) => handleChange('zahlungsziel_tage', e.target.value === '' ? 5 : parseInt(e.target.value, 10))}
                  />
                </div>
              </div>
              <p className="text-[11px] text-slate-400 mt-2">
                Skonto 0 % / 0 Tage = erscheint nicht auf der Rechnung · Zahlungsziel erscheint immer
              </p>
            </div>

            {(isCustomerStatus(lead?.status) || isLostStatus(lead?.status)) && (
              <div className="space-y-2 md:col-span-2">
                <Label>Bewertung</Label>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <StarRating
                    rating={formData.rating}
                    onRate={(rating) => handleChange('rating', rating)}
                    size="lg"
                  />
                  <span className="text-sm text-slate-600">
                    {formData.rating ? `${formData.rating} von 5 Sternen` : 'Nicht bewertet'}
                  </span>
                </div>
              </div>
            )}

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="notes">Notizen</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Zusätzliche Notizen..."
                rows={3}
              />
            </div>
          </div>

          {/* ── Ansprechpartner (opsiyonel, einklappbar) ─────────────
              Mevcut maske düzenine dokunmadan EK bölüm: kapalı başlar,
              "+ Ansprechpartner hinzufügen" ile açılır. */}
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            {!apOpen && ansprechpartner.length === 0 ? (
              <button
                type="button"
                onClick={addAnsprechpartner}
                className="w-full min-h-[48px] flex items-center justify-center gap-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <Plus className="w-4 h-4" /> Ansprechpartner hinzufügen
                <span className="text-[10px] text-slate-400 font-normal">(optional)</span>
              </button>
            ) : (
              <div>
                <button
                  type="button"
                  onClick={() => setApOpen(!apOpen)}
                  className="w-full min-h-[44px] flex items-center gap-2 px-4 text-sm font-semibold text-slate-700 bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
                >
                  <Users className="w-4 h-4 text-slate-500" />
                  Ansprechpartner ({ansprechpartner.length})
                  <ChevronDown className={cn('w-4 h-4 ml-auto text-slate-400 transition-transform', apOpen && 'rotate-180')} />
                </button>
                {apOpen && (
                  <div className="p-3 space-y-3">
                    {ansprechpartner.map((p, idx) => (
                      <div key={idx} className="p-3 bg-slate-50/60 border border-slate-200 rounded-lg space-y-2 relative">
                        <button
                          type="button"
                          aria-label="Ansprechpartner entfernen"
                          onClick={() => removeAnsprechpartner(idx)}
                          className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-md text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pr-8">
                          <div>
                            <Label className="text-[10px] uppercase text-slate-500">Name</Label>
                            <Input value={p.name}
                              onChange={(e) => updateAnsprechpartner(idx, 'name', e.target.value)}
                              placeholder="z.B. Max Mustermann" className="h-10" />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase text-slate-500">Position</Label>
                            <Input value={p.position}
                              onChange={(e) => updateAnsprechpartner(idx, 'position', e.target.value)}
                              placeholder="z.B. Einkauf, Küchenchef" className="h-10" />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase text-slate-500">Telefon</Label>
                            <Input type="tel" value={p.telefon}
                              onChange={(e) => updateAnsprechpartner(idx, 'telefon', e.target.value)}
                              placeholder="+49 …" className="h-10" />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase text-slate-500">E-Mail</Label>
                            <Input type="email" value={p.email}
                              onChange={(e) => updateAnsprechpartner(idx, 'email', e.target.value)}
                              placeholder="name@firma.de" className="h-10" />
                          </div>
                        </div>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm"
                      onClick={addAnsprechpartner} className="h-9 gap-1 text-xs">
                      <Plus className="w-3.5 h-3.5" /> Weiterer Ansprechpartner
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
              <X className="w-4 h-4 mr-2" />
              Abbrechen
            </Button>
            <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800">
              <Save className="w-4 h-4 mr-2" />
              {isEditing ? 'Speichern' : 'Erstellen'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

const OFFEN_IN_SHOPIFY = ['Neu', 'Kommissioniert', 'Versandbereit'];

/** Admin-Schalter "Versand über Shopify (DPD)" + Hinweis auf bereits gesendete Bestellungen */
function VersandSchalter({ checked, onChange, leadId }) {
  const { data: bereitsInShopify = [] } = useQuery({
    queryKey: ['versand-wechsel-offen', leadId],
    enabled: !!leadId,
    queryFn: async () => {
      const { data, error } = await initSupabase().from('orders')
        .select('order_number, status, shopify_mirror_id')
        .eq('lead_id', leadId).in('status', OFFEN_IN_SHOPIFY).like('shopify_mirror_id', 'gid://%');
      if (error) return [];
      return data || [];
    },
  });
  return (
    <div className="space-y-2 md:col-span-2 rounded-lg border border-slate-200 p-3">
      <div className="flex items-center justify-between gap-4">
        <Label htmlFor="shopify_versand" className="cursor-pointer">
          <span className="block text-sm font-medium">Versand über Shopify (DPD)</span>
          <span className="block text-[11px] text-slate-500 font-normal mt-0.5">
            {checked
              ? 'Aktiv: Bestellungen werden an Shopify gesendet, DPD erstellt das Etikett.'
              : 'Aus – eigene Zustellung: Bestellungen bleiben nur in der App, kein DPD-Etikett.'}
          </span>
        </Label>
        <Switch id="shopify_versand" checked={checked} onCheckedChange={onChange} />
      </div>
      {!checked && bereitsInShopify.length > 0 && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
          {bereitsInShopify.length} offene Bestellung(en) liegen bereits in Shopify ({bereitsInShopify.map((o) => o.order_number).join(', ')}).
          Die Änderung gilt nur für noch nicht gesendete Bestellungen — dort bitte das DPD-Etikett ggf. manuell stoppen.
        </p>
      )}
    </div>
  );
}
